import React, { useState, useRef, useCallback } from 'react';
import { DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  maxFiles?: number;
  selectedFiles?: File[];
  error?: string | null;
  disabled?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  acceptedTypes = ['.pdf', '.txt', '.doc', '.docx'],
  maxSizeMB = 10,
  maxFiles = 5,
  selectedFiles = [],
  error = null,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `O arquivo é muito grande. Tamanho máximo: ${maxSizeMB}MB`;
    }

    // Check file type by extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidExtension = acceptedTypes.some(type => 
      type.toLowerCase() === fileExtension
    );

    if (!isValidExtension) {
      return `Tipo de arquivo não suportado. Tipos aceitos: ${acceptedTypes.join(', ')}`;
    }

    // Additional MIME type validation to prevent PPTX and other unwanted formats
    const validMimeTypes = [
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (file.type && !validMimeTypes.includes(file.type)) {
      return `Tipo de arquivo não suportado. Tipos aceitos: ${acceptedTypes.join(', ')}`;
    }

    return null;
  }, [acceptedTypes, maxSizeMB]);

  const handleFiles = useCallback((incoming: File[]) => {
    const result = [...selectedFiles];
    for (const file of incoming) {
      if (result.length >= maxFiles) {
        toast.error(`Máximo de ${maxFiles} arquivos`);
        break;
      }
      if (result.some(f => f.name === file.name && f.size === file.size)) {
        continue;
      }
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(`${file.name}: ${validationError}`);
        continue;
      }
      result.push(file);
    }
    if (result.length !== selectedFiles.length) {
      onFilesChange(result);
    }
  }, [validateFile, onFilesChange, selectedFiles, maxFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
    e.target.value = '';
  }, [handleFiles]);

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    onFilesChange(selectedFiles.filter((_, i) => i !== index));
  };

  const canAddMore = selectedFiles.length < maxFiles;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
        multiple
      />

      {canAddMore && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer
            transition-all duration-200
            ${isDragging 
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 scale-[1.02]' 
              : error
              ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
          `}
        >
          <DocumentArrowUpIcon className={`
            mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-3
            ${error 
              ? 'text-red-500 dark:text-red-400' 
              : 'text-gray-400 dark:text-gray-500'
            }
          `} />
          
          <p className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
            {isDragging ? 'Solte os arquivos aqui' : 'Clique para selecionar ou arraste até ' + maxFiles + ' arquivos'}
          </p>
          
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {acceptedTypes.join(', ').toUpperCase()} • Máximo {maxSizeMB}MB cada
          </p>

          {error && (
            <div className="mt-3 flex items-center justify-center space-x-2 text-red-600 dark:text-red-400">
              <ExclamationCircleIcon className="h-5 w-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}
        </div>
      )}

      {selectedFiles.length > 0 && (
        <ul className={`space-y-2 ${canAddMore ? 'mt-3' : ''}`}>
          {selectedFiles.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center space-x-3 border-2 rounded-lg p-3 border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20"
            >
              <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-green-500 dark:text-green-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatFileSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={(e) => handleRemove(e, index)}
                disabled={disabled}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed p-1"
                title="Remover arquivo"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
